import { useState, useEffect } from "react";
import { Timer } from "lucide-react";

interface CountdownTickerProps {
  targetDate: string;
  variant?: "compact" | "large" | "card";
}

function calcRemaining(target: Date) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    totalMs: diff,
  };
}

export function CountdownTicker({ targetDate, variant = "compact" }: CountdownTickerProps) {
  const target = new Date(targetDate);
  const [remaining, setRemaining] = useState(() => calcRemaining(target));
  // Keep the first client render structurally identical to the SSR output so
  // hydration can't mismatch when the countdown crosses zero between the two.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(calcRemaining(target));
    }, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  const zero = { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };

  if (!remaining && mounted) {
    const now = new Date();
    const isToday = target.toDateString() === now.toDateString();
    if (!isToday) return null;
    // Fall through with zeroed values + drawingToday flag
  }

  const drawingToday = !remaining && mounted;
  const r = remaining || zero;

  const isUrgent = drawingToday || r.totalMs < 3600000;

  const drawingTodayBadge = drawingToday ? (
    <div className="flex items-center justify-center gap-1.5">
      <Timer className="h-4 w-4 text-accent animate-pulse" />
      <span className="text-accent font-bold text-sm">Drawing Today!</span>
    </div>
  ) : null;

  if (variant === "card") {
    const units = [
      { value: r.days, label: "Days" },
      { value: r.hours, label: "Hrs" },
      { value: r.minutes, label: "Min" },
      { value: r.seconds, label: "Sec" },
    ];

    return (
      <div className="space-y-1.5">
        {drawingTodayBadge || (
          <div className="flex items-center justify-center gap-1.5">
            <Timer className={`h-3.5 w-3.5 text-accent ${isUrgent ? "animate-pulse" : ""}`} />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Next Draw</span>
          </div>
        )}
        <div className={`flex gap-1.5 ${isUrgent ? "animate-pulse" : ""}`}>
          {units.map(({ value, label }) => (
            <div
              key={label}
              className={`flex flex-col items-center rounded-md px-2 py-1 min-w-[2.5rem] ${
                isUrgent
                  ? "bg-accent/20 ring-1 ring-accent/40"
                  : "bg-secondary"
              }`}
            >
              <span suppressHydrationWarning className="font-display text-base font-bold tabular-nums text-foreground">
                {String(value).padStart(2, "0")}
              </span>
              <span className="text-[10px] text-muted-foreground leading-none">{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "large") {
    return (
      <div>
        {drawingTodayBadge && <div className="mb-2">{drawingTodayBadge}</div>}
        <div className="flex gap-3">
          {[
            { value: r.days, label: "Days" },
            { value: r.hours, label: "Hrs" },
            { value: r.minutes, label: "Min" },
            { value: r.seconds, label: "Sec" },
          ].map(({ value, label }) => (
            <div key={label} className="flex flex-col items-center">
              <span suppressHydrationWarning className="font-display text-2xl md:text-3xl font-bold tabular-nums text-primary">
                {String(value).padStart(2, "0")}
              </span>
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Compact variant
  const parts: string[] = [];
  if (r.days > 0) parts.push(`${r.days}d`);
  parts.push(`${String(r.hours).padStart(2, "0")}h`);
  parts.push(`${String(r.minutes).padStart(2, "0")}m`);
  parts.push(`${String(r.seconds).padStart(2, "0")}s`);

  return (
    <span suppressHydrationWarning className="font-mono text-sm tabular-nums text-muted-foreground">
      {drawingTodayBadge && <span className="mr-2">{drawingTodayBadge}</span>}
      {parts.join(" ")}
    </span>
  );
}
